import React from 'react';
import { Link } from 'react-router-dom';
import { LUX_CSS, useLuxMotion } from './LuxShared';
import ContactButton from './ContactButton';

const POINTS = [
  ['Product-as-a-Service', 'A productised delivery engine — scoped, staffed and shipping — without you standing up a team from scratch.'],
  ['Design-led engagements', 'For existing enterprise programmes, we design the solution and the operating model, then build it alongside your people.'],
  ['Enterprise-grade from day one', 'Security, compliance, observability and scale are the starting line, not a later phase.'],
  ['Veterans who’ve shipped at scale', 'Senior pods led by industry veterans who have delivered inside large, regulated organisations.'],
];

const STEPS = [
  ['01', 'Align', 'One senior room, your stakeholders and ours, agrees the scope, the architecture, and what "done" means — before a single person is resourced.'],
  ['02', 'Staff', 'A senior-only pod is assembled around the programme. No bench, no ramp-up, no junior hand-offs.'],
  ['03', 'Ship', 'Sprints ship into production from week one. Compliance, security and observability are built in, not bolted on later.'],
  ['04', 'Scale', "Once it's live, we stay — as your long-term delivery partner — or hand over a clean, documented, zero-lock-in codebase your team owns outright."],
];

const WHO = [
  'Enterprises with a mandate to ship, but no bandwidth to build the team',
  'Organisations replacing an underperforming vendor relationship',
  'Regulated industries — finance, healthcare, infrastructure — that need compliance built in, not bolted on',
  'Internal platform teams that need a senior pod to absorb overflow without diluting their own standards',
];

const COMPARE = [
  ['Junior-heavy teams, slow ramp-up', 'Senior-only pods from day one'],
  ['Weeks of onboarding before anything ships', 'Production sprints from week one'],
  ['Lock-in by design', 'Clean, documented, zero-lock-in handover'],
  ['Security and compliance bolted on later', 'Built into the architecture from sprint one'],
];

const FAQ = [
  ['How fast can a pod start?', 'Most engagements move from scoping to first sprint within two weeks. The room, the roadmap and the "done" definition come first — resourcing follows.'],
  ['Do you work alongside our existing team, or replace them?', 'Both models exist. Some clients plug us in as a pod inside their org; others hand us a whole programme to design and deliver end to end.'],
  ['What about security and compliance?', 'Built in from day one — not a hardening phase we bolt on at the end. Your compliance requirements shape the architecture from the first sprint.'],
  ['What do we own at the end?', 'Everything. Clean repo, documented decisions, zero lock-in — whether you keep us on as a long-term partner or take it fully in-house.'],
];

const CatalystLux = () => {
  const rootRef = useLuxMotion();
  return (
    <div className="fx-root" ref={rootRef}>
      <style>{LUX_CSS}</style>

      <section className="fx-hero fx-mono fx-tall">
        <video className="fx-hero-video" muted loop playsInline autoPlay preload="auto" src="/nh-media/catalyst-hero.mp4" />
        <div className="fx-orb fx-orb-a" />
        <div className="fx-orb fx-orb-b" />
        <div className="fx-hero-scrim" />
        <div className="fx-hero-inner">
          <h1 className="fx-reveal">Firebucks <span className="fx-accent">Catalyst.</span></h1>
          <p className="fx-reveal">Product-as-a-Service for the enterprise — and design-led delivery for the engagements you already have. Ship at scale without building the machine yourself.</p>
          <div className="fx-hero-cta fx-pop">
            <Link className="fx-btn" to="/contact-us">Talk to Firebucks <span className="fx-arr">→</span></Link>
            <Link className="fx-btn fx-ghost" to="/firstspark">For founders → Firstspark</Link>
          </div>
        </div>
      </section>

      <section className="fx-story">
        <div className="fx-story-inner fx-reveal">
          <span className="fx-story-eyebrow">The idea</span>
          <p>Most enterprises don't need another vendor. They need a <span className="fx-accent">delivery engine that already works</span> — scoped, staffed and shipping from week one, with the assurance a large organisation demands.</p>
        </div>
      </section>

      <section className="fx-section">
        <div className="fx-wrap">
          <div className="fx-lead fx-reveal">
            <h2>The delivery engine, <span className="fx-accent">productised.</span></h2>
            <p>Catalyst is for organisations that need to move like a startup with the assurance of an enterprise. We plug a senior, accountable pod into your world — or design the solution outright — and take it all the way to production.</p>
          </div>
          <div className="fx-points">
            {POINTS.map(([h, p]) => (
              <div key={h} className="fx-point fx-reveal"><h4>{h}</h4><p>{p}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="fx-section fx-soft">
        <div className="fx-wrap">
          <div className="fx-head fx-reveal"><h2>How Catalyst <span className="fx-accent">works</span></h2></div>
          <div className="fx-method">
            <div className="fx-rail"><div className="fx-rail-track"><div className="fx-rail-fill" /><div className="fx-rail-pct">0</div></div></div>
            <div className="fx-msteps">
              {STEPS.map(([n, h, p]) => (
                <div key={n} className="fx-mstep fx-reveal">
                  <div className="fx-mstep-head"><span className="fx-mstep-n">/{n}</span><h4>{h}</h4><span className="fx-mstep-plus">+</span></div>
                  <div className="fx-mstep-body"><p>{p}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="fx-section">
        <div className="fx-wrap">
          <div className="fx-head fx-reveal"><h2>Who Catalyst <span className="fx-accent">is for</span></h2></div>
          <div className="fx-checklist">
            {WHO.map((w) => (<div key={w} className="fx-check fx-reveal">{w}</div>))}
          </div>
        </div>
      </section>

      <section className="fx-section fx-soft">
        <div className="fx-wrap">
          <div className="fx-head fx-reveal"><h2>Why teams choose Catalyst <span className="fx-accent">over a typical vendor</span></h2></div>
          <div className="fx-compare fx-reveal">
            <table>
              <thead><tr><th>Typical Vendor</th><th className="fx-col-win">Firebucks Catalyst</th></tr></thead>
              <tbody>
                {COMPARE.map(([a, b]) => (
                  <tr key={a}><td>{a}</td><td className="fx-col-win">{b}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="fx-section">
        <div className="fx-wrap">
          <div className="fx-lead fx-reveal">
            <h2>Global, and <span className="fx-accent">senior everywhere</span></h2>
            <p>Delivered from senior engineering hubs in Bengaluru, Charlotte and Zurich — with the assurance of a partner who's shipped inside regulated, large-scale organisations before.</p>
          </div>
          <div className="fx-hubs fx-pop">
            <span className="fx-hub">Bengaluru</span>
            <span className="fx-hub">Charlotte</span>
            <span className="fx-hub">Zurich</span>
          </div>
        </div>
      </section>

      <section className="fx-section fx-soft">
        <div className="fx-wrap">
          <div className="fx-head fx-reveal"><h2>Questions, <span className="fx-accent">answered</span></h2></div>
          <div className="fx-faq">
            {FAQ.map(([q, a]) => (
              <div key={q} className="fx-faq-item fx-reveal">
                <div className="fx-faq-q">{q}<span className="fx-faq-plus">+</span></div>
                <div className="fx-faq-a"><p>{a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactButton
        title="Ready to scale delivery?"
        subtitle="Tell us the programme. We'll show you the shortest credible path to shipping it."
      />
    </div>
  );
};

export default CatalystLux;
